/**
 * The one screen a fresh Create can end on besides a working game (Decision
 * 175). A provider outage waits: past its quick retries the server tries hourly
 * for a day, and Studio shows the game when it's ready. Everything else,
 * including an outage that outlasts the day, keeps the idea and offers Start again.
 * Create costs the creator nothing yet, so nothing is refunded.
 */
import { Link } from 'react-router-dom';
import { composedOutageWaitingV1, type ComposedCreateRunV1 } from '@djgg/creations';
import type { StudioAnalysisState } from './flow.js';
import { plainAnalysisError } from './creatorCopy.js';

export type CreateLastResortKind = 'waiting' | 'start-again';

export const CREATE_WAITING_MESSAGE = 'We’re still working on this. Our tools are busy right now, so we’ll keep trying. Your game will show up in your Studio when it’s ready.';
export const CREATE_START_AGAIN_MESSAGE = 'We couldn’t finish this one. Your idea is saved, so you can start again.';

/** Where a fresh Create stands once nothing more runs by itself, or null while it does. */
export function createLastResortV1({ analysisStatus, analysisError, run, firstBuild }: {
  analysisStatus: StudioAnalysisState | null;
  analysisError: string;
  run: Pick<ComposedCreateRunV1, 'status' | 'temporaryRetry'> | null;
  /** `composedFirstBuildNextV1` for the first build, once one exists. */
  firstBuild: 'wait' | 'repair' | 'finished' | 'last-resort' | null;
}): { kind: CreateLastResortKind; message: string } | null {
  if (firstBuild === 'last-resort') return { kind: 'start-again', message: CREATE_START_AGAIN_MESSAGE };
  if (firstBuild) return null;
  if (run && composedOutageWaitingV1(run)) return { kind: 'waiting', message: CREATE_WAITING_MESSAGE };
  if (run?.status === 'failed') return { kind: 'start-again', message: CREATE_START_AGAIN_MESSAGE };
  if (analysisStatus === 'failed' || analysisStatus === 'stale') {
    const plain = plainAnalysisError(analysisError);
    return { kind: 'start-again', message: plain === plainAnalysisError('') ? CREATE_START_AGAIN_MESSAGE : plain };
  }
  return null;
}

export function CreateLastResort({ kind, message, onStartAgain }: {
  kind: CreateLastResortKind; message: string; onStartAgain: () => void;
}) {
  return <section className="creation-last-resort" data-testid="create-last-resort" data-kind={kind} aria-live="polite">
    <p role="status">{message}</p>
    {kind === 'start-again'
      ? <button type="button" className="btn btn--primary" onClick={onStartAgain} data-testid="create-start-again">Start again</button>
      : <Link className="btn" to="/studio">Back to Studio</Link>}
  </section>;
}
